<template>
  <section class="audience-picker">
    <header class="audience-picker__head">
      <label class="audience-picker__search">
        <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
        <input
          type="search"
          v-model="creators.search"
          placeholder="Search creators"
          aria-label="Search creators within this audience"
        >
      </label>

      <p class="audience-picker__summary" role="status">
        <template v-if="selected.length">
          <strong>{{ selected.length }}</strong> picked &mdash; only these will receive it
        </template>
        <template v-else-if="audienceCount !== null">
          Nobody picked &mdash; all <strong>{{ audienceCount.toLocaleString() }}</strong>
          matching creators will receive it
        </template>
      </p>

      <button v-if="selected.length" type="button" class="audience-picker__clear" @click="selected = []">
        Clear selection
      </button>
    </header>

    <!--
      Only a failure that left nothing on screen replaces the list. One that hit
      page four is reported in the footer instead: the rows above it are still
      good, and some of them may already be ticked.
    -->
    <p v-if="creators.error && !creators.items.length" class="audience-picker__empty">{{ creators.error }}</p>
    <p v-else-if="creators.loading && !creators.items.length" class="audience-picker__empty">Loading creators&hellip;</p>
    <p v-else-if="creators.isEmpty" class="audience-picker__empty">
      {{ filters.length ? 'No creator matches these filters.' : 'No creator can receive marketing email.' }}
    </p>

    <!--
      The list scrolls; the page does not. `data-load-more` marks the row the
      controller watches - it fetches the next page when that row comes into
      view, so the fetch starts while there is still most of a screen to read.
    -->
    <ul v-else :ref="creators.observe" class="audience-picker__list">
      <li
        v-for="(creator, index) in creators.items"
        :key="creator.id"
        class="audience-picker__row"
        :class="{ 'is-picked': selected.includes(creator.id) }"
        :data-load-more="index === creators.triggerIndex ? '' : null"
      >
        <label>
          <!--
            The native box is hidden rather than removed: it stays the thing
            that is focused, checked and read out, and the avatar beside it is
            only its picture. Same technique as the option chips in the filter
            rows above.
          -->
          <input type="checkbox" :value="creator.id" v-model="selected">

          <!--
            The creator's own photo, or the monogram every other creator list in
            the client area falls back to. `tone` is derived from who they are,
            so the same person is the same colour on every screen.
          -->
          <span class="audience-picker__avatar" :class="`audience-picker__avatar--tone-${creator.tone}`" aria-hidden="true">
            <!--
              A picture that cannot be shown looks exactly like no picture to
              the person reading the row, so it is treated as one: dropping the
              url falls the row back to the monogram rather than leaving an
              empty disc where a face was promised.
            -->
            <img
              v-if="creator.avatar"
              :src="creator.avatar"
              alt=""
              loading="lazy"
              @error="creator.avatar = ''"
            >
            <template v-else>{{ creator.initials }}</template>
            <i class="fa-solid fa-check audience-picker__tick"></i>
          </span>

          <span class="audience-picker__identity">
            <span class="audience-picker__name">{{ creator.name || 'Unnamed creator' }}</span>
            <span class="audience-picker__meta">{{ creator.email }}</span>
            <span v-if="creator.phone" class="audience-picker__meta">{{ creator.phone }}</span>
          </span>
        </label>
      </li>
    </ul>

    <footer v-if="creators.items.length" class="audience-picker__more">
      <span v-if="creators.loading">Loading more&hellip;</span>

      <template v-else-if="creators.needsManualLoad">
        <span v-if="creators.error" class="audience-picker__more-error">{{ creators.error }}</span>
        <button type="button" @click="creators.loadMore">{{ creators.error ? 'Try again' : 'Load more' }}</button>
      </template>

      <!--
        The estimate beside the filters counts the segment, not the search. Once
        the box has something in it that number is the wrong denominator, so the
        line drops it rather than claiming "50 of 4,300" over a search that
        matched three people.
      -->
      <span v-else-if="creators.cursor">Scroll for more</span>
      <span v-else-if="creators.search">{{ creators.items.length.toLocaleString() }} found</span>
      <span v-else>All {{ creators.items.length.toLocaleString() }} shown</span>
    </footer>
  </section>
</template>

<script>
import { computed, watch } from 'vue'
import { usePagedList } from './paged-list.mjs'

/**
 * The creators an audience matches, so the client can narrow it by hand.
 *
 * Split out of AudienceBuilder, which was two components in one file: a filter
 * builder that owns what the step submits, and this - a list that owns nothing
 * but which rows are on screen. They share exactly one value, the selection,
 * and it is passed as a model rather than reached for.
 *
 * The selection deliberately lives up in the builder. It is part of the step's
 * payload, it survives this list being reloaded or emptied, and the estimate
 * beside the filters is computed from it.
 */
export default {
  name: 'CreatorPicker',

  props: {
    // Endpoint returning { items: [{ id, name, email, phone }], next_cursor }.
    url: {
      type: String,
      required: true
    },
    // The audience filters as the step would submit them. Sent with every
    // request, and a change to them is a different list.
    filters: {
      type: Array,
      required: true
    },
    // The picked creator ids.
    modelValue: {
      type: Array,
      required: false,
      default: () => []
    },
    // How many creators the filters alone match, for the line that explains
    // what happens when nothing is picked. Null while it is being counted.
    audienceCount: {
      type: Number,
      required: false,
      default: null
    }
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const creators = usePagedList({
      url: props.url,
      method: 'POST',
      // Read per request rather than captured: the filters change while this
      // component is alive.
      params: () => ({ audience_filters: props.filters })
    })

    const selected = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })

    // A different set of filters is a different list. The controller handles
    // its own search box; this is the other input it cannot see.
    watch(() => props.filters, () => creators.reloadSoon())

    creators.reload()

    return { creators, selected }
  }
}
</script>
